import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Scale, Loader2, Users, Clock, FileCheck2, ShieldAlert, History, Package } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAFF_ROLES = ["deal_manager", "admin", "super_admin", "compliance_director", "senior_verification_manager", "compliance_reviewer"];
const CHANNELS = ["legal", "compliance", "inspection", "logistics", "insurance", "customs", "accounting", "financing", "escrow", "mediation", "general"];

function statusCls(s) {
  if (["completed", "carrier_accepted", "assigned"].includes(s)) return "bg-emerald-100 text-emerald-700";
  if (["no_provider_available", "escalated", "no_carrier_available"].includes(s)) return "bg-red-100 text-red-700";
  if (["providers_invited", "carrier_offered", "work_in_progress", "deliverable_submitted"].includes(s)) return "bg-blue-100 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

function Countdown({ iso }) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  const overdue = ms < 0;
  const hrs = Math.round(Math.abs(ms) / 3.6e6);
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${overdue ? "text-red-600" : "text-slate-500"}`}>
      <Clock size={10} /> {overdue ? `overdue ${hrs}h` : `${hrs}h left`}
    </span>
  );
}

export default function RoutingPanel({ deal }) {
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [channel, setChannel] = useState("legal");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${deal.deal_id}/routing`);
      setReqs(data.requests || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [deal.deal_id]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const detect = () => act("detect", () => api.post(`/deals/${deal.deal_id}/routing/detect`), "AI detected service needs");
  const create = () => act("create", () => api.post(`/deals/${deal.deal_id}/routing`, { channel }), `Requested ${channel} service`);
  const buildPool = (r) => act(`pool-${r.request_id}`, () => api.post(`/routing/${r.request_id}/build-pool`), "Qualified provider pool built");
  const acceptOffer = (r) => act(`acc-${r.request_id}`, () => api.post(`/routing/${r.request_id}/accept`, { coi_no_conflict: true, coi_statement: "No conflict of interest" }), "Offer accepted");
  const declineOffer = (r) => act(`dec-${r.request_id}`, () => api.post(`/routing/${r.request_id}/decline`, { reason: "declined" }), "Offer declined");
  const evidence = (r) => act(`ev-${r.request_id}`, () => api.post(`/routing/${r.request_id}/evidence-package`), "Evidence package versioned");

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="routing-panel">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Scale size={15} /> Professional services & routing</h4>
        {isStaff && (
          <button data-testid="routing-detect-btn" onClick={detect} disabled={busy === "detect"}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:border-slate-400">AI detect needs</button>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select data-testid="routing-channel-select" value={channel} onChange={(e) => setChannel(e.target.value)}
          className="h-9 rounded-md border border-slate-300 px-3 text-sm">
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button data-testid="routing-create-btn" onClick={create} disabled={busy === "create"}
          className="h-9 rounded-md bg-[#2563EB] px-3 text-sm font-medium text-white disabled:opacity-60">Request service</button>
      </div>

      {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-3" data-testid="routing-list">
          {reqs.length === 0 && <p className="text-sm text-slate-400">No service requests yet.</p>}
          {reqs.map((r) => (
            <div key={r.request_id} data-testid={`svc-${r.request_id}`} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium text-[#0A0F1C]">
                  <FileCheck2 size={14} className="text-slate-400" /> {r.channel}
                  {r.urgency && r.urgency !== "standard" && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold uppercase text-amber-800">{r.urgency}</span>}
                </span>
                <span data-testid={`svc-status-${r.request_id}`} className={`rounded px-2 py-0.5 text-xs ${statusCls(r.status)}`}>{r.status?.replace(/_/g, " ")}</span>
              </div>

              {r.deadlines?.provider_acceptance && (
                <div className="mt-1.5"><Countdown iso={r.deadlines.provider_acceptance} /></div>
              )}

              {/* Qualified pool + explanations */}
              {r.ranked?.length > 0 && (
                <div className="mt-2" data-testid={`svc-pool-${r.request_id}`}>
                  <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-slate-400"><Users size={11} /> Qualified pool ({r.ranked.length})</div>
                  <ul className="space-y-0.5 text-[11px] text-slate-500">
                    {r.ranked.slice(0, 4).map((p, i) => (
                      <li key={i}>#{i + 1} {p.provider_id} — {p.why}</li>
                    ))}
                  </ul>
                  {r.excluded?.length > 0 && <p className="mt-1 text-[11px] text-slate-400">{r.excluded.length} excluded (eligibility gate)</p>}
                </div>
              )}

              {/* Offers */}
              {r.offers?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`svc-offers-${r.request_id}`}>
                  {r.offers.map((o, i) => (
                    <span key={i} className={`rounded px-1.5 py-0.5 text-[11px] ${statusCls(o.status)}`}>{o.provider_id?.slice(0, 12)}: {o.status}</span>
                  ))}
                </div>
              )}

              {/* Deliverables */}
              {r.deliverables?.length > 0 && (
                <div className="mt-2 text-[11px] text-slate-500" data-testid={`svc-deliverables-${r.request_id}`}>
                  Deliverables: {r.deliverables.map((d) => `${d.title} (${d.state})`).join(", ")}
                </div>
              )}

              {/* Escalations + replacement history */}
              {r.escalations?.length > 0 && (
                <p className="mt-1.5 flex items-center gap-1 text-[11px] text-red-600" data-testid={`svc-escalations-${r.request_id}`}>
                  <ShieldAlert size={11} /> {r.escalations.length} escalation(s): {r.escalations[r.escalations.length - 1].level}
                </p>
              )}
              {r.provider_history?.length > 0 && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400" data-testid={`svc-history-${r.request_id}`}>
                  <History size={11} /> {r.provider_history.length} replacement(s)
                </p>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5">
                {isStaff && (!r.pool || r.pool.length === 0) && (
                  <button data-testid={`svc-buildpool-${r.request_id}`} onClick={() => buildPool(r)} disabled={busy === `pool-${r.request_id}`}
                    className="h-7 rounded bg-slate-800 px-2 text-xs text-white">Build pool</button>
                )}
                {r.status === "providers_invited" && (
                  <>
                    <button data-testid={`svc-accept-${r.request_id}`} onClick={() => acceptOffer(r)} disabled={busy === `acc-${r.request_id}`}
                      className="h-7 rounded bg-[#65A30D] px-2 text-xs text-white">Accept offer</button>
                    <button data-testid={`svc-decline-${r.request_id}`} onClick={() => declineOffer(r)} disabled={busy === `dec-${r.request_id}`}
                      className="h-7 rounded border border-slate-300 px-2 text-xs text-slate-600">Decline</button>
                  </>
                )}
                <button data-testid={`svc-evidence-${r.request_id}`} onClick={() => evidence(r)} disabled={busy === `ev-${r.request_id}`}
                  className="h-7 rounded border border-slate-300 px-2 text-xs text-slate-600 flex items-center gap-1"><Package size={11} /> Evidence pkg</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
